import {
  isFlightSavedItemData,
  isLeFlightSavedItemData,
  type TransportConnection,
  type TransportOption,
  type TripItineraryData,
} from 'tripPlanner/api/types'
import { resolveSearchDefaults } from 'tripPlanner/utils/resolveSearchDefaults'
import { parseSearchParams } from '../ShortlistedFlightsSection/flightShortlistUtils'

const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}/

export interface FlightSearchDrawerParams {
  originAirportCode?: string
  originAirportName?: string
  destinationAirportCode?: string
  destinationAirportName?: string
  departureDate?: string
  returnDate?: string
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
}

function getFlightDateParam(value: string | undefined): string | undefined {
  return value?.match(DATE_PARAM_PATTERN)?.[0]
}

export function findTransportConnectionForOption(
  itineraryData: TripItineraryData | undefined,
  itemId: TransportOption['itemId'] | undefined,
): TransportConnection | undefined {
  if (itemId === undefined) return undefined
  const targetItemId = String(itemId)

  for (const stop of itineraryData?.stops ?? []) {
    for (const transport of stop.transports ?? []) {
      if (transport.options.some(option => String(option.itemId) === targetItemId)) {
        return transport
      }
    }
  }

  return undefined
}

export function getFlightSearchDrawerParamsFromTransport(
  option: TransportOption,
  transport: TransportConnection | undefined,
  returnOption?: TransportOption,
): FlightSearchDrawerParams {
  const savedItemData = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnLegSavedItemDataInput = returnOption?.savedItemData ?? option.returnLeg?.savedItemData
  const returnLegSavedItemData = isFlightSavedItemData(returnLegSavedItemDataInput) ? returnLegSavedItemDataInput : undefined
  const parsedSearchParams = isLeFlightSavedItemData(savedItemData)
    ? parseSearchParams(savedItemData.searchParams)
    : undefined
  const firstLeg = parsedSearchParams?.itinerary[0]
  const returnLeg = parsedSearchParams?.itinerary[1]
  const flightSearchDefaults = transport?.flightSearchDefaults

  return {
    originAirportCode: firstLeg?.departureAirport ?? savedItemData?.departureAirport ?? flightSearchDefaults?.originAirportCode,
    originAirportName: firstLeg?.departureAirport || savedItemData?.departureAirport ? undefined : flightSearchDefaults?.originAirportName,
    destinationAirportCode: firstLeg?.arrivalAirport ?? savedItemData?.arrivalAirport ?? flightSearchDefaults?.destinationAirportCode,
    destinationAirportName: firstLeg?.arrivalAirport || savedItemData?.arrivalAirport ? undefined : flightSearchDefaults?.destinationAirportName,
    departureDate: firstLeg?.departureDate ?? savedItemData?.departureDate ?? getFlightDateParam(savedItemData?.departureTime) ?? flightSearchDefaults?.departureDate,
    returnDate: returnLeg?.departureDate ?? returnLegSavedItemData?.departureDate ?? getFlightDateParam(returnLegSavedItemData?.departureTime),
    fareType: returnOption || option.returnLeg ? 'return' : parsedSearchParams?.fareType,
    fareClass: parsedSearchParams?.fareClass,
    occupants: parsedSearchParams?.occupancies[0],
  }
}

export function applyRecentFlightSearchDefaults(
  params: FlightSearchDrawerParams,
  itineraryData: TripItineraryData | undefined,
  stopId: number | null | undefined,
  options?: { preferParamsAsSelected?: boolean },
): FlightSearchDrawerParams {
  const resolved = resolveSearchDefaults({
    surface: 'flights',
    stopId,
    recentSearches: itineraryData?.recentSearches,
    ...(options?.preferParamsAsSelected ? { selected: params } : {}),
    savedFlightParams: params,
    indicativeSearchMonth: itineraryData?.indicativeSearchMonth,
    productDefaults: { occupants: { adults: 1 } },
  })

  if (resolved.surface !== 'flights') {
    return params
  }

  return {
    ...params,
    originAirportCode: resolved.originAirportCode ?? params.originAirportCode,
    originAirportName: resolved.originAirportName ?? params.originAirportName,
    destinationAirportCode: resolved.destinationAirportCode ?? params.destinationAirportCode,
    destinationAirportName: resolved.destinationAirportName ?? params.destinationAirportName,
    departureDate: resolved.departureDate ?? params.departureDate,
    returnDate: resolved.returnDate ?? params.returnDate,
    fareType: resolved.fareType ?? params.fareType,
    fareClass: resolved.fareClass ?? params.fareClass,
    occupants: resolved.occupants ?? params.occupants,
  }
}
